import React from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { CONTENTS_ROUTE } from "@/src/components/routes/routes";
import {
  ANALYST_ADMIN,
  EDITOR_ADMIN,
  OWNER,
  PROJECT_ADMIN,
} from "@/src/constants/roles";
import useAbility from "@/src/hooks/useAbility";
import useMediaCreationDate from "@/src/hooks/useMediaCreationDate";
import usePeriodFilter from "@/src/hooks/usePeriodFilter";
import { getElementByUuid, getIcon } from "@/src/utils/data/Content";

import ContentCountriesMapCard from "./ContentCountriesMapCard";
import ContentCountriesRankedCard from "./ContentCountriesRankedCard";
import ContentDevicesCard from "./ContentDevicesCard";
import {
  contentViewsGraphQuery,
  liveStatsQuery,
  mediaKpiQuery,
} from "./contentItem.queries";
import { isLiveOnAir } from "./contentItem.utils";
import ContentItemHeader from "./ContentItemHeader";
import ContentKpiGrid from "./ContentKpiGrid";
import ContentViewsCard from "./ContentViewsCard";
import LiveViewersSection from "./LiveViewersSection";
import MediaDetailedViewsTable from "./MediaDetailedViewsTable";
import AnalyticsPage from "../AnalyticsPage";
import AnalyticsPeriodPicker from "../charts/AnalyticsPeriodPicker";

import type { JSX } from "react";

import type { GetMediasItem } from "@/src/api/MediaAPI.types";
import type { Role } from "@/src/constants/roles";
import type { AppState } from "@/src/reducers";

const ADMIN_ROLE_LIST: Role[] = [
  OWNER,
  EDITOR_ADMIN,
  PROJECT_ADMIN,
  ANALYST_ADMIN,
];

function ContentItemAnalytics(): JSX.Element {
  const { projectSlug, id } = useParams<{ projectSlug: string; id: string }>();
  const { t } = useTranslation("editor-analytics");
  const { hasRole, canAccess } = useAbility();
  const { period, setPeriod } = usePeriodFilter();
  const mediaCreationDate = useMediaCreationDate();

  const contents = useSelector(
    (state: AppState) => state.fetchReducer.contents,
  );
  const organizationOptions = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options,
  );

  const content: GetMediasItem | null = getElementByUuid(id, contents);
  const isLive = content?.type === "live";

  const mediaKpiResult = useQuery(mediaKpiQuery(projectSlug, id));
  const viewsGraphResult = useQuery({
    ...contentViewsGraphQuery(projectSlug, id, period),
    enabled: !isLive,
  });
  const { data: liveStats } = useQuery({
    ...liveStatsQuery(id),
    enabled: isLive,
  });

  const mediaKpi = mediaKpiResult.data?.data[0];
  const viewsInPeriod = viewsGraphResult.data?.data.reduce(
    (total, point) => total + point.views,
    0,
  );
  const isOnAir = isLive && isLiveOnAir(liveStats?.status);
  const title = content?.label ?? mediaKpi?.label ?? "";
  const contentUrl =
    content?.type && canAccess(organizationOptions).contents
      ? `${CONTENTS_ROUTE}/${content.type}/${id}`
      : null;

  return (
    <AnalyticsPage
      actions={
        <AnalyticsPeriodPicker
          minDate={mediaCreationDate}
          onChange={setPeriod}
          period={period}
        />
      }
      breadcrumbList={[
        { name: t("editor-menu:analytics"), url: "/analytics/content" },
        { name: title },
      ]}
      heading={
        <ContentItemHeader
          contentUrl={contentUrl}
          iconName={content ? getIcon(content) : null}
          imageUrl={mediaKpi?.cover_picture_16_6_url}
          isOnAir={isOnAir}
          title={title}
        />
      }
    >
      <ContentKpiGrid
        isAdmin={hasRole(ADMIN_ROLE_LIST)}
        isLive={isLive}
        mediaKpi={mediaKpi}
        mediaKpiStatus={mediaKpiResult}
        viewsInPeriod={viewsInPeriod}
        viewsInPeriodStatus={viewsGraphResult}
      />
      {isLive ? (
        <LiveViewersSection
          beginDate={content.begin_date}
          contentId={id}
          isOnAir={isOnAir}
          liveEnd={content.live_end}
        />
      ) : (
        <ContentViewsCard contentId={id} period={period} />
      )}
      <ContentCountriesMapCard contentId={id} period={period} />
      <div className="grid gap-4 @3xl:grid-cols-2">
        <ContentCountriesRankedCard contentId={id} period={period} />
        <ContentDevicesCard contentId={id} period={period} />
      </div>
      <MediaDetailedViewsTable contentId={id} period={period} />
    </AnalyticsPage>
  );
}

export default ContentItemAnalytics;
